// src/modules/calendar/pages/InstructorCalendarView.jsx
// Vista de instructor: REUSA el mismo calendario del admin (CalendarPage) en modo
// solo-lectura, limitado a los eventos del instructor. El admin puede cambiar de
// instructor con el selector. Al hacer clic en un evento se abre el detalle +
// "Solicitar reprogramación".
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import { useAuth as useAuthContext } from '../../../hooks/AuthContext';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { collection, getDocs, addDoc, serverTimestamp, updateDoc, doc } from 'firebase/firestore';
import { db } from '../../../services/firebase';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { FiAlertCircle, FiCalendar, FiClock, FiSend } from 'react-icons/fi';
import toast from 'react-hot-toast';
import InstructorSearchSelect from '../components/InstructorSearchSelect';
import CalendarPage from './CalendarPage';
import { Eye, EyeOff } from 'lucide-react';
import { viewAsUrl } from '../../../utils/viewAs';

const InstructorCalendarView = () => {
    const { user } = useAuth();
    const { user: ctxUser } = useAuthContext();
    const isAdmin = ctxUser?.role === 'admin';
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const adminViewId = searchParams.get('id');
    const effectiveAdminTarget = isAdmin && adminViewId ? adminViewId : null;
    // "Ver como instructor" (?ver=1): sin barra de admin, con el menú del instructor.
    const viewAs = !!effectiveAdminTarget && searchParams.get('ver') === '1';

    const [instructor, setInstructor] = useState(null);
    const [instructorsList, setInstructorsList] = useState([]);
    const [loading, setLoading] = useState(true);

    // Reprogramación
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [selectedCourse, setSelectedCourse] = useState(null);
    const [reprogramMsg, setReprogramMsg] = useState('');
    const [sendingRequest, setSendingRequest] = useState(false);

    // Lista de instructores (para el switcher de admin)
    useEffect(() => {
        if (!isAdmin) return;
        let active = true;
        getDocs(collection(db, 'instructors'))
            .then(snap => { if (active) setInstructorsList(snap.docs.map(d => ({ id: d.id, ...d.data() }))); })
            .catch(() => { });
        return () => { active = false; };
    }, [isAdmin]);

    // Resolver el instructor (por email, o forzado por admin via ?id=)
    useEffect(() => {
        if (!user?.email) return;
        let active = true;
        (async () => {
            setLoading(true);
            try {
                const snap = await getDocs(collection(db, 'instructors'));
                const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
                let inst = null;
                if (effectiveAdminTarget) {
                    inst = all.find(i => i.id === effectiveAdminTarget);
                } else {
                    inst = all.find(i => i.email === user.email)
                        || all.find(i => i.linkedUserId === user.uid);
                    if (!inst && user.displayName) {
                        const un = user.displayName.toLowerCase();
                        inst = all.find(i => i.name && (un.includes(i.name.toLowerCase()) || i.name.toLowerCase().includes(un)));
                    }
                    if (inst && !inst.email && user.email) {
                        try { await updateDoc(doc(db, 'instructors', inst.id), { email: user.email, linkedUserId: user.uid }); } catch { }
                    }
                }
                if (active) setInstructor(inst || null);
            } catch (err) {
                console.error('Error resolviendo instructor:', err);
            } finally {
                if (active) setLoading(false);
            }
        })();
        return () => { active = false; };
    }, [user, effectiveAdminTarget]);

    const handleRequestReprogramming = async () => {
        if (!selectedEvent || !reprogramMsg.trim() || !instructor) return;
        setSendingRequest(true);
        try {
            await addDoc(collection(db, 'reprogramming_requests'), {
                eventId: selectedEvent.id,
                eventTitle: selectedEvent.title,
                instructorId: instructor.id,
                instructorName: instructor.name,
                instructorEmail: user.email,
                message: reprogramMsg,
                status: 'pending',
                createdAt: serverTimestamp(),
                originalDate: selectedEvent.start,
            });
            toast.success('Solicitud de reprogramación enviada');
            setReprogramMsg('');
            setSelectedEvent(null);
            setSelectedCourse(null);
        } catch { toast.error('Error al enviar solicitud'); }
        setSendingRequest(false);
    };

    const AdminSwitcher = ({ placeholder }) => (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 shrink-0">
            <div className="max-w-5xl mx-auto flex items-center gap-3">
                <span className="text-[11px] font-semibold text-amber-700 uppercase">Modo admin:</span>
                <div className="flex-1 max-w-xs">
                    <InstructorSearchSelect
                        instructors={instructorsList}
                        value={instructor?.id || ''}
                        onChange={(id) => { if (id && id !== instructor?.id) navigate(`/mi-calendario?id=${id}`); }}
                        placeholder={placeholder}
                    />
                </div>
                {instructor && (
                    <button onClick={() => navigate(viewAsUrl(instructor.id))}
                        className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-amber-300 text-amber-800 text-xs font-semibold hover:bg-amber-100 transition"
                        title="Ver la app exactamente como la ve este instructor">
                        <Eye size={14} /> Ver como instructor
                    </button>
                )}
            </div>
        </div>
    );

    const ViewAsBar = () => (
        <div className="bg-indigo-600 text-white px-4 py-2 shrink-0">
            <div className="max-w-5xl mx-auto flex items-center gap-3 text-xs">
                <Eye size={15} className="shrink-0" />
                <span className="flex-1 min-w-0 truncate">
                    Viendo como <b>{instructor?.name || 'instructor'}</b> — así ve su calendario (solo lectura, tu sesión no cambia)
                </span>
                <button onClick={() => navigate(`/mi-calendario?id=${instructor?.id || ''}`)}
                    className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/15 hover:bg-white/25 font-semibold transition shrink-0">
                    <EyeOff size={13} /> Salir
                </button>
            </div>
        </div>
    );

    if (loading) {
        return (
            <div className="h-full flex items-center justify-center bg-gray-50">
                <div className="w-8 h-8 border-3 border-blue-600 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    if (!instructor) {
        if (isAdmin) {
            return (
                <div className="h-full flex flex-col bg-gray-50">
                    <AdminSwitcher placeholder="Selecciona un instructor..." />
                    <div className="flex-1 flex items-center justify-center">
                        <div className="text-center max-w-md">
                            <FiCalendar size={48} className="text-indigo-400 mx-auto mb-4" />
                            <h2 className="text-xl font-bold text-gray-800">Calendario de instructores</h2>
                            <p className="text-sm text-gray-500 mt-2">Selecciona un instructor arriba para ver su calendario tal como lo ve él.</p>
                        </div>
                    </div>
                </div>
            );
        }
        return (
            <div className="h-full flex items-center justify-center bg-gray-50">
                <div className="text-center max-w-md">
                    <FiAlertCircle size={48} className="text-amber-500 mx-auto mb-4" />
                    <h2 className="text-xl font-bold text-gray-800">No estás vinculado como instructor</h2>
                    <p className="text-sm text-gray-500 mt-2">Contacta al administrador para vincular tu cuenta ({user.email}) con tu perfil de instructor.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="h-full flex flex-col bg-gray-50">
            {viewAs ? <ViewAsBar /> : isAdmin && <AdminSwitcher placeholder="Cambiar instructor..." />}

            {/* Calendario admin reutilizado en modo solo-lectura, filtrado al instructor */}
            <div className="flex-1 min-h-0">
                <CalendarPage
                    instructorMode
                    viewerInstructorId={instructor.id}
                    onInstructorEventClick={(ev, course) => { setSelectedEvent(ev); setSelectedCourse(course); }}
                />
            </div>

            {/* Detalle + solicitar reprogramación */}
            {selectedEvent && (
                <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[60] p-4" onClick={() => { setSelectedEvent(null); setSelectedCourse(null); }}>
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden" onClick={e => e.stopPropagation()}>
                        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-5 text-white">
                            <h2 className="text-lg font-bold">{selectedEvent.title}</h2>
                            <p className="text-xs opacity-75 mt-1">
                                <FiClock size={11} className="inline mr-1" />
                                {format(new Date(selectedEvent.start), "EEEE d 'de' MMMM, HH:mm", { locale: es })} - {format(new Date(selectedEvent.end), 'HH:mm')}
                            </p>
                        </div>
                        <div className="p-5 space-y-3">
                            {/* Enlaces de la clase */}
                            {(() => {
                                if (selectedEvent.type === 'clase') {
                                    if (!selectedEvent.claseLiveUrl) return null;
                                    return (
                                        <div className="bg-blue-50 rounded-xl p-3 space-y-1.5">
                                            <p className="text-[10px] font-bold text-blue-700 uppercase">🎥 Enlace clase en vivo</p>
                                            <a href={selectedEvent.claseLiveUrl} target="_blank" rel="noopener noreferrer"
                                                className="text-xs text-blue-600 hover:underline block truncate">{selectedEvent.claseLiveUrl}</a>
                                            <button onClick={() => { navigator.clipboard.writeText(selectedEvent.claseLiveUrl); toast.success('Enlace copiado'); }}
                                                className="text-[10px] bg-blue-100 text-blue-700 px-2 py-1 rounded-lg hover:bg-blue-200 font-medium">Copiar enlace</button>
                                        </div>
                                    );
                                }
                                const vars = selectedCourse?.defaultVariables;
                                if (!vars?.zoomLink) return null;
                                return (
                                    <div className="bg-blue-50 rounded-xl p-3 space-y-1.5">
                                        <p className="text-[10px] font-bold text-blue-700 uppercase">Enlace de clase</p>
                                        <a href={vars.zoomLink} target="_blank" rel="noopener noreferrer"
                                            className="text-xs text-blue-600 hover:underline block truncate">{vars.zoomLink}</a>
                                        <div className="flex items-center gap-3 text-[11px] text-gray-600">
                                            {vars.meetingId && <span>ID: <b>{vars.meetingId}</b></span>}
                                            {vars.password && <span>Pass: <b>{vars.password}</b></span>}
                                        </div>
                                        <button onClick={() => { navigator.clipboard.writeText(vars.zoomLink); toast.success('Enlace copiado'); }}
                                            className="text-[10px] bg-blue-100 text-blue-700 px-2 py-1 rounded-lg hover:bg-blue-200 font-medium">Copiar enlace Zoom</button>
                                    </div>
                                );
                            })()}
                            <div>
                                <label className="text-xs font-semibold text-gray-700">Motivo / Propuesta de nueva fecha</label>
                                <textarea value={reprogramMsg} onChange={e => setReprogramMsg(e.target.value)}
                                    placeholder="Ej: No puedo ese día, propongo el jueves 27 a las 7pm..."
                                    rows={3}
                                    className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-400 outline-none resize-none" />
                            </div>
                            <div className="flex gap-2">
                                <button onClick={() => { setSelectedEvent(null); setSelectedCourse(null); }}
                                    className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50">Cerrar</button>
                                <button onClick={handleRequestReprogramming} disabled={!reprogramMsg.trim() || sendingRequest}
                                    className="flex-1 py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 flex items-center justify-center gap-2">
                                    {sendingRequest ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <FiSend size={14} />}
                                    Solicitar reprogramación
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default InstructorCalendarView;
